import type {
  AppArtifactDto,
  AppExampleFile,
  AppManifest,
  AppRunDetail,
  AppRunResultDto,
} from '@api/apps';
import type { AskUserQuestion, MessageAttachment } from '@hooks/useChat';
import type {
  KitApp,
  KitContext,
  KitError,
  KitFile,
  KitQuestion,
  KitResult,
  KitRun,
  KitState,
} from '../../../appKit/host';
import type { AppRunLiveState } from '../useAppRunStream';

export interface IndexedFile {
  readonly url: string;
  readonly name: string;
  readonly contentType: string;
  /** Null when the host does not know it until it has the bytes. */
  readonly size: number | null;
}

/** Every id the page has been told about; it may read or download these and nothing else. */
export type FileIndex = ReadonlyMap<string, IndexedFile>;

export interface HostRunFlags {
  readonly busy: boolean;
  readonly stopping: boolean;
  readonly error: KitError | null;
}

const artifactUrl = (artifactId: string): string => `/api/v1/artifacts/${encodeURIComponent(artifactId)}/download`;

const nameOf = (url: string): string => url.split(/[?#]/)[0].split('/').pop() ?? url;

class IndexBuilder {
  readonly index = new Map<string, IndexedFile>();

  add(id: string, file: IndexedFile): string {
    this.index.set(id, file);
    return id;
  }

  artifact(artifact: AppArtifactDto): KitFile {
    this.add(artifact.artifactId, {
      url: artifact.downloadUrl ?? artifactUrl(artifact.artifactId),
      name: artifact.name,
      contentType: artifact.contentType,
      size: artifact.fileSize,
    });
    return { id: artifact.artifactId, name: artifact.name, contentType: artifact.contentType, size: artifact.fileSize };
  }

  attachment(attachment: MessageAttachment): KitFile {
    if (attachment.url) {
      this.add(attachment.attachmentId, {
        url: attachment.url,
        name: attachment.filename,
        contentType: attachment.contentType,
        size: attachment.fileSize,
      });
    }
    // A preview with no name of its own is a reduced copy of the same kind, such as a smaller image.
    const previewName = attachment.previewName ?? attachment.filename;
    const previewId = attachment.previewUrl
      ? this.add(`${attachment.attachmentId}:preview`, {
          url: attachment.previewUrl,
          name: previewName,
          contentType: attachment.previewName ? '' : attachment.contentType,
          size: attachment.previewFileSize ?? null,
        })
      : null;
    return {
      id: attachment.attachmentId,
      name: attachment.filename,
      contentType: attachment.contentType,
      size: attachment.fileSize,
      previewId,
      previewName: previewId ? previewName : null,
    };
  }

  example(file: AppExampleFile, id: string): KitFile {
    this.add(id, { url: file.url, name: file.filename, contentType: file.contentType, size: null });
    const previewId = file.previewUrl
      ? this.add(`${id}:preview`, { url: file.previewUrl, name: nameOf(file.previewUrl), contentType: '', size: null })
      : null;
    return {
      id,
      name: file.filename,
      contentType: file.contentType,
      size: null,
      previewId,
      previewName: file.previewUrl ? nameOf(file.previewUrl) : null,
    };
  }

  result(result: AppRunResultDto): KitResult {
    const previewId = result.previewUrl
      ? this.add(`${result.resultId}:preview`, {
          url: result.previewUrl,
          name: result.previewName ?? 'preview.glb',
          contentType: 'model/gltf-binary',
          size: null,
        })
      : null;
    return {
      version: result.version,
      files: result.artifacts.map((artifact) => this.artifact(artifact)),
      previewId,
      previewName: previewId ? (result.previewName ?? 'preview.glb') : null,
      requestText: result.requestText ?? null,
      notesText: result.notesText ?? null,
      createdAt: result.createdAt,
    };
  }

  question(question: AskUserQuestion): KitQuestion {
    const { imageArtifactId, ...rest } = question;
    if (!imageArtifactId) return rest;
    this.add(imageArtifactId, { url: artifactUrl(imageArtifactId), name: 'drawing.png', contentType: 'image/png', size: null });
    return { ...rest, imageId: imageArtifactId };
  }
}

/**
 * The step pointer only moves during a run's first turn: the server never
 * moves it backwards, so on later turns it names where the first one ended.
 */
export const stepPointerOf = (run: AppRunDetail, liveStepId: string | null): string | null => {
  if (run.results.length > 0) return liveStepId;
  if (run.status === 'FAILED') return run.failureStepId ?? run.currentStepId ?? null;
  return liveStepId ?? run.currentStepId ?? null;
};

const NO_DELIVERABLE: KitApp['deliverable'] = { extensions: [], artifactTypes: [] };

const appOf = (manifest: AppManifest, files: IndexBuilder): KitApp => {
  const example = manifest.presentation?.example ?? null;
  return {
    key: manifest.key,
    version: manifest.version,
    name: manifest.name,
    tagline: manifest.tagline,
    start: manifest.start ?? 'FILES',
    usesAgent: manifest.usesAgent !== false,
    inputs: manifest.inputs,
    options: manifest.options,
    steps: manifest.steps ?? [],
    deliverable: manifest.deliverable ?? NO_DELIVERABLE,
    example: example
      ? {
          inputs: example.inputs.map((file, index) => files.example(file, `example:input:${index}`)),
          result: example.result ? files.example(example.result, 'example:result') : null,
          options: example.options ?? null,
          prompt: example.prompt ?? null,
        }
      : null,
  };
};

const runOf = (run: AppRunDetail, live: AppRunLiveState, flags: HostRunFlags, files: IndexBuilder): KitRun => {
  const turnVersion = new Map(run.results.map((result) => [result.resultId, result.version]));
  const questions = run.pendingQuestions.length > 0 ? run.pendingQuestions : (live.liveQuestions ?? []);
  return {
    id: run.runId,
    status: run.status,
    stepId: stepPointerOf(run, live.liveStepId),
    failedStepId: run.failureStepId ?? null,
    activity: live.activity,
    busy: flags.busy,
    stopping: flags.stopping,
    error: flags.error ?? (run.status === 'FAILED' && run.failureReason ? { code: 'FAILED', message: run.failureReason } : null),
    options: run.options,
    inputs: run.inputs.map((input) => files.attachment(input)),
    results: run.results.map((result) => files.result(result)),
    turns: run.turns.map((turn) => ({
      id: turn.turnId,
      requestText: turn.requestText ?? null,
      replyText: turn.replyText ?? null,
      version: turn.resultId ? (turnVersion.get(turn.resultId) ?? null) : null,
      createdAt: turn.createdAt,
    })),
    pendingRequest: run.pendingRequestText ?? null,
    questions: questions.map((question) => files.question(question)),
  };
};

export const bridgeStateOf = (
  rev: number,
  manifest: AppManifest,
  run: AppRunDetail | null,
  live: AppRunLiveState,
  flags: HostRunFlags,
  context: KitContext,
): { state: KitState; files: FileIndex } => {
  const files = new IndexBuilder();
  const state: KitState = {
    rev,
    app: appOf(manifest, files),
    context,
    run: run ? runOf(run, live, flags, files) : null,
  };
  return { state, files: files.index };
};
